/**
 * Generador de gafetes de acreditación.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * MEDIDAS, QUE AQUÍ NO SON DECORATIVAS
 * ──────────────────────────────────────────────────────────────────────────
 *
 * El gafete mide 53.98 × 85.60 mm: la norma ISO/IEC 7810 ID-1 —la de una
 * tarjeta de crédito— puesta DE PIE. Importa que sea exacta porque se imprime,
 * se corta y se cuelga de un portagafetes.
 *
 * El acomodo en carta sale de dividir, no de elegir:
 *
 *   ancho    215.9 / 53.98 = 3.99  →  3 columnas   (sobran 53.96, 26.98 por lado)
 *   alto     279.4 / 85.60 = 3.26  →  3 filas      (sobran 22.60, 11.30 por lado)
 *
 * 9 por hoja, 68.9% del papel aprovechado. La cuarta columna se queda a 0.02 mm
 * de caber: 4 × 53.98 = 215.92 contra 215.9 de hoja. Dos centésimas de
 * milímetro, pero además dejaría margen cero, así que no es una opción.
 *
 * Esos 11.3 mm de margen vertical son lo que vuelve innecesaria cualquier
 * alternativa de «margen amplio»: el área no imprimible de una impresora
 * normal es de 4 a 6 mm, así que este acomodo ya sale entero en cualquiera.
 * Acostado hacían falta dos acomodos porque el máximo dejaba 4.75 mm y se
 * arriesgaba a que la primera y la última fila salieran recortadas.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * POR QUÉ LA LEYENDA DICE LO QUE DICE
 * ──────────────────────────────────────────────────────────────────────────
 *
 * Además de acreditar, NIEGA expresamente representar a una autoridad
 * electoral o a un partido. No es exceso de cautela: §Bloque 0 prohíbe al
 * encuestador portar prendas o artículos de cualquier partido, y la
 * credencial es justo el objeto que un entrevistado podría confundir con una
 * identificación oficial. Decirlo por escrito protege al entrevistado de esa
 * confusión y a la firma de la acusación de haberla provocado.
 */

import { useMemo, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api, type AdminUser } from '../api/client';

/** Datos de la firma que acredita, en un solo sitio. */
const EMISOR = {
  razonSocial: 'Calimetría',
  domicilio: 'Av. P.º de la Reforma 296, Piso 25, Juárez, Cuauhtémoc, 06600 Ciudad de México, CDMX',
  telefono: '229 300 9017',
  representante: 'Martín Ontiveros Torres',
  cargo: 'Representante legal',
};

const LEYENDA =
  'Acredita a la persona identificada como encuestador(a) autorizado(a) por Calimetría para el ' +
  'levantamiento del Sondeo Pre-Electoral de Tecate 2027. No representa a autoridad electoral ni ' +
  'a partido político alguno.';

/**
 * La rejilla se declara una sola vez y el CSS la lee de `--cols` y `--rows`.
 *
 * Cuántos caben por hoja y cuánto margen queda son consecuencias aritméticas
 * de estos dos números, así que no se escriben a mano. Si la paginación dijera
 * 9 y la rejilla tuviera 2 filas, la tercera terna se saldría de la hoja y no
 * se imprimiría: nueve gafetes pedidos, seis salidos y ningún aviso.
 */
const COLUMNAS = 3;
const FILAS = 3;
const POR_HOJA = COLUMNAS * FILAS;

const ANCHO_CARTA_MM = 215.9;
const ALTO_CARTA_MM = 279.4;
const ANCHO_TARJETA_MM = 53.98;
const ALTO_TARJETA_MM = 85.6;

const MARGEN_X_MM = (ANCHO_CARTA_MM - COLUMNAS * ANCHO_TARJETA_MM) / 2;
const MARGEN_Y_MM = (ALTO_CARTA_MM - FILAS * ALTO_TARJETA_MM) / 2;

export function BadgesPage() {
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users() });

  const [soloActivos, setSoloActivos] = useState(true);
  const [soloCampo, setSoloCampo] = useState(true);
  const [marcasDeCorte, setMarcasDeCorte] = useState(true);

  const seleccion = useMemo<AdminUser[]>(() => {
    const todos = users.data ?? [];
    return todos
      .filter((u) => (soloActivos ? u.isActive : true))
      // El gafete acredita para tocar puertas. Un analista que nunca sale a
      // campo no necesita uno, y emitirlo de más diluye lo que significa.
      .filter((u) => (soloCampo ? u.role === 'encuestador' || u.role === 'supervisor' : true))
      .sort((a, b) => a.code.localeCompare(b.code, 'es'));
  }, [users.data, soloActivos, soloCampo]);

  const hojas = Math.ceil(seleccion.length / POR_HOJA);

  if (users.isLoading) {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <>
      {/* Todo lo que no sea el pliego se oculta al imprimir. */}
      <div className="no-print">
        <div className="page-head">
          <h1 className="page-title">Gafetes de acreditación</h1>
          <p className="page-sub">
            Tamaño tarjeta de crédito en vertical (54 × 85.6 mm, norma ISO/IEC 7810 ID-1), uno por
            cada cuenta. Se imprimen {POR_HOJA} por hoja carta.
          </p>
        </div>

        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">Qué se imprime</h2>
              <p className="panel__note">
                Al imprimir, elige tamaño <strong>Carta</strong>, escala <strong>100%</strong> y
                activa <strong>gráficos de fondo</strong>. Sin escala al 100% las medidas dejan de
                ser las de una tarjeta; sin gráficos de fondo el gafete sale sin la banda de marca.
              </p>
            </div>
          </div>

          <div className="stack">
            <label className="row">
              <input
                type="checkbox"
                checked={soloCampo}
                onChange={(e) => setSoloCampo(e.target.checked)}
              />
              <span>
                Sólo quienes salen a campo
                <span className="faint"> · encuestadores y supervisores</span>
              </span>
            </label>
            <label className="row">
              <input
                type="checkbox"
                checked={soloActivos}
                onChange={(e) => setSoloActivos(e.target.checked)}
              />
              <span>
                Sólo cuentas activas
                <span className="faint"> · una cuenta dada de baja no debe acreditar a nadie</span>
              </span>
            </label>
            <label className="row">
              <input
                type="checkbox"
                checked={marcasDeCorte}
                onChange={(e) => setMarcasDeCorte(e.target.checked)}
              />
              <span>
                Marcas de corte
                <span className="faint"> · guías finas para cortar con guillotina</span>
              </span>
            </label>
          </div>

          <p className="panel__note" style={{ marginTop: 16 }}>
            Quedan {MARGEN_X_MM.toFixed(1)} mm de margen a los lados y {MARGEN_Y_MM.toFixed(1)} mm
            arriba y abajo, de sobra para que ninguna impresora recorte la primera ni la última
            fila.
          </p>

          <div className="row row--between" style={{ marginTop: 20 }}>
            <div>
              <span className="strong">
                {seleccion.length} gafete{seleccion.length === 1 ? '' : 's'}
              </span>
              <span className="faint">
                {' '}
                · {hojas} hoja{hojas === 1 ? '' : 's'} carta
                {seleccion.length % POR_HOJA !== 0 && seleccion.length > 0
                  ? ` · la última con ${seleccion.length % POR_HOJA}`
                  : ''}
              </span>
            </div>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => window.print()}
              disabled={seleccion.length === 0}
            >
              Imprimir
            </button>
          </div>

          {seleccion.length === 0 ? (
            <div className="callout callout--info" style={{ marginTop: 16, marginBottom: 0 }}>
              Ningún usuario cumple los filtros. Si todavía no has creado las cuentas de campo,
              hazlo desde Usuarios.
            </div>
          ) : null}
        </div>
      </div>

      {/* ------------------------------ El pliego ----------------------------- */}

      <div className={`sheet-set${marcasDeCorte ? ' sheet-set--marks' : ''}`}>
        {Array.from({ length: hojas }, (_, h) => (
          <div
            className="sheet"
            key={h}
            style={{ '--cols': COLUMNAS, '--rows': FILAS } as CSSProperties}
          >
            {seleccion.slice(h * POR_HOJA, (h + 1) * POR_HOJA).map((u) => (
              <Badge key={u.id} user={u} />
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * El cuerpo del nombre se ajusta a su longitud.
 *
 * De pie quedan 47 mm útiles de ancho, la mitad que acostado, y los nombres
 * mexicanos completos son largos: dos nombres de pila y dos apellidos pasan
 * de 40 caracteres con facilidad. Un cuerpo fijo obligaría a elegir entre que
 * los cortos se vean pequeños o que los largos desborden. Los umbrales salen
 * de medir cuántos caracteres entran por renglón a cada tamaño.
 */
function escalaDelNombre(nombre: string): string {
  if (nombre.length <= 18) return 'xl';
  if (nombre.length <= 30) return 'l';
  if (nombre.length <= 42) return 'm';
  return 's';
}

function Badge({ user }: { user: AdminUser }) {
  return (
    <article className="badge" aria-label={`Gafete de ${user.name}`}>
      {/* Banda de marca: es lo que lee alguien a tres metros, antes que el texto. */}
      <header className="badge__head">
        <div className="badge__logo" role="img" aria-label="Calimetría" />
      </header>

      {/* A caballo sobre el borde de la banda, como en una credencial real. */}
      <span className="badge__role">{user.roleLabel}</span>

      <div className="badge__identity">
        <h2 className="badge__name" data-escala={escalaDelNombre(user.name)}>
          {user.name}
        </h2>
        <p className="badge__code">Folio {user.code}</p>
      </div>

      <p className="badge__legal">{LEYENDA}</p>

      <div className="badge__sign">
        <div className="badge__signature" role="img" aria-label="Firma" />
        <div className="badge__signline" />
        <p className="badge__signer">{EMISOR.representante}</p>
        <p className="badge__signrole">{EMISOR.cargo}</p>
      </div>

      <footer className="badge__foot">
        <span className="badge__addr">{EMISOR.domicilio}</span>
        <span className="badge__phone">Tel. {EMISOR.telefono}</span>
      </footer>
    </article>
  );
}
